/** 새 서명을 그릴 때 쓰는 매장 공통 펜 굵기(px). 고객 공개 입력폼·판매점 서명 패드가 함께 쓴다. */
export const SIGNATURE_PEN_WIDTH_MIN = 4;
export const SIGNATURE_PEN_WIDTH_MAX = 20;
export const DEFAULT_SIGNATURE_PEN_WIDTH = 12;

/** JSONB/API 값이 잘못됐거나 아직 없으면 기존 새 서명 기본값(12px)으로 폴백한다. */
export function normalizeSignaturePenWidth(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_SIGNATURE_PEN_WIDTH;
  }
  const rounded = Math.round(value);
  if (rounded < SIGNATURE_PEN_WIDTH_MIN || rounded > SIGNATURE_PEN_WIDTH_MAX) {
    return DEFAULT_SIGNATURE_PEN_WIDTH;
  }
  return rounded;
}

/**
 * 슬라이더 값이 "이 크기의 서명 칸"을 기준으로 정해졌다고 보는 기준 높이(CSS px).
 *
 * 왜 필요한가 — 출력물에서의 획 굵기는 "펜 굵기 ÷ 서명 칸 높이"가 정한다. 신청서 출력은
 * 서명 PNG 를 서식의 서명 박스에 비율 유지로 축소해 얹기 때문이다(scripts/form-render/render.py
 * 의 contain 축소). 그래서 같은 12px 이라도 **전체화면 서명판(고객 폰)** 과 **140px 짜리 작은
 * 패드(판매점 모달)** 에서 그리면 출력물에서는 서로 다른 굵기가 된다.
 *
 * 기준값 220 은 세로 휴대폰(390×844 급)에서 전체화면 서명판이 잡는 서명 칸 높이다 — 서명판은
 * 가로로 돌아가므로 화면 가로폭(390)에서 여백·안내문구·버튼 줄을 뺀 값이 서명 칸이 된다.
 * 기종마다 조금씩 다르므로 근사값이다(요청자에게도 "완전히 같아지진 않는다"고 안내됨).
 */
export const SIGNATURE_REFERENCE_CANVAS_HEIGHT = 220;

/**
 * 서명 칸 높이에 맞춰 펜 굵기를 환산한다 — **출력된 신청서에서 비슷한 굵기로 보이게** 하는 보정.
 *
 * 기준(고객 폰 전체화면 서명판)보다 작은 칸이면 그만큼 얇게 그린다. 화면에서는 얇아 보이지만
 * 출력할 때 축소가 덜 되므로 결과가 맞는다. 0.25px 단위로 반올림한다 — 정수로 끊으면 가장
 * 얇은 4px 에서 오차가 10% 가까이 나고, 그보다 잘게 쪼개도 눈에 보이지 않는다.
 */
export function scaleSignaturePenWidth(
  penWidth: unknown,
  canvasHeightPx: number,
): number {
  const base = normalizeSignaturePenWidth(penWidth);
  if (!Number.isFinite(canvasHeightPx) || canvasHeightPx <= 0) return base;
  const scaled = (base * canvasHeightPx) / SIGNATURE_REFERENCE_CANVAS_HEIGHT;
  // 아무리 작은 칸이어도 획이 사라지면 안 되니 0.5px 을 하한으로 둔다.
  return Math.max(0.5, Math.round(scaled * 4) / 4);
}
